import React, { useState, useEffect } from 'react';
import {
  Card, Typography, Button, Form, Input, Switch,
  message, Table, Space, Popconfirm, Modal, Tag, Row, Col,
  Descriptions, Divider, Select, Upload,
} from 'antd';
import {
  ArrowLeftOutlined, PlusOutlined, EditOutlined,
  DeleteOutlined, AppstoreOutlined, EyeOutlined, UploadOutlined,
} from '@ant-design/icons';

const { Title, Text } = Typography;

const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:')) return url;
  const baseUrl = import.meta.env.VITE_API_BASE_URL || '';
  const cleanBase = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
  const cleanUrl = url.startsWith('/') ? url : `/${url}`;
  return `${cleanBase}${cleanUrl}`;
};

const BusinessTypes = ({ setView }) => {
  const [loading, setLoading] = useState(false);
  const [businessTypes, setBusinessTypes] = useState([]);
  const [saving, setSaving] = useState(false);

  // Edit / Add modal
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingBusinessType, setEditingBusinessType] = useState(null);
  const [imageFiles, setImageFiles] = useState({});
  const [removedImages, setRemovedImages] = useState({});

  // View (detail) modal
  const [isViewModalVisible, setIsViewModalVisible] = useState(false);
  const [viewingBusinessType, setViewingBusinessType] = useState(null);

  const [form] = Form.useForm();

  useEffect(() => {
    fetchBusinessTypes();
  }, []);

  // ── API Calls ──────────────────────────────────────────────────────────────

  const fetchBusinessTypes = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL}/kirazee/api/v1/admin/business-types/`
      );
      const data = await response.json();
      if (Array.isArray(data)) {
        setBusinessTypes(data);
      } else if (data.data && Array.isArray(data.data)) {
        setBusinessTypes(data.data);
      } else if (data.success || data.data) {
        setBusinessTypes(data.data || []);
      } else {
        setBusinessTypes([]);
      }
    } catch (error) {
      console.error('Error fetching business types:', error);
      message.error('Failed to fetch business types');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingBusinessType(null);
    form.resetFields();
    form.setFieldsValue({ status: true });
    setImageFiles({});
    setRemovedImages({});
    setIsModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingBusinessType(record);
    setImageFiles({});
    setRemovedImages({});
    form.setFieldsValue({
      code: record.code,
      type: record.type,
      title: record.title,
      caption: record.caption,
      status: record.status,
      svg: record.svg,
      categories: record.categories || [],
      order_types: record.order_types || [],
      logo: record.logo,
      banner: record.banner,
      mobile_logo: record.mobile_logo,
      mobile_banner: record.mobile_banner,
      // theme sub-fields flattened
      theme_gradient: record.theme?.gradient,
      theme_primary_color: record.theme?.primary_color,
      theme_secondary_color: record.theme?.secondary_color,
      theme_background_color: record.theme?.background_color,
      theme_text_color: record.theme?.text_color,
    });
    setIsModalVisible(true);
  };

  const handleView = (record) => {
    setViewingBusinessType(record);
    setIsViewModalVisible(true);
  };

  const handleDelete = async (code) => {
    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL}/kirazee/api/v1/admin/business-types/${code}/`,
        { method: 'DELETE' }
      );
      if (response.ok) {
        message.success('Business Type deleted successfully');
        fetchBusinessTypes();
      } else {
        const data = await response.json();
        message.error(data.message || 'Failed to delete Business Type');
      }
    } catch (error) {
      console.error('Error deleting business type:', error);
      message.error('An error occurred while deleting');
    }
  };

  const handleModalSubmit = async (values) => {
    setSaving(true);
    try {
      const url = editingBusinessType
        ? `${import.meta.env.VITE_API_BASE_URL}/kirazee/api/v1/admin/business-types/${editingBusinessType.code}/`
        : `${import.meta.env.VITE_API_BASE_URL}/kirazee/api/v1/admin/business-types/`;
      const method = editingBusinessType ? 'PUT' : 'POST';

      const hasFiles = Object.values(imageFiles).some((f) => f instanceof File);

      let body;
      let headers = {};

      const themeObj = {
        gradient: values.theme_gradient,
        primary_color: values.theme_primary_color,
        secondary_color: values.theme_secondary_color,
        background_color: values.theme_background_color,
        text_color: values.theme_text_color,
      };

      if (hasFiles) {
        // Send as multipart/form-data
        const formData = new FormData();
        ['code', 'type', 'title', 'caption', 'svg'].forEach((f) => {
          if (values[f] != null) formData.append(f, values[f]);
        });
        formData.append('status', values.status ? 'true' : 'false');
        if (values.categories) formData.append('categories', JSON.stringify(values.categories));
        if (values.order_types) formData.append('order_types', JSON.stringify(values.order_types));
        formData.append('theme', JSON.stringify(themeObj));
        ['logo', 'banner', 'mobile_logo', 'mobile_banner'].forEach((field) => {
          if (imageFiles[field] instanceof File) {
            formData.append(field, imageFiles[field]);
          } else if (removedImages[field]) {
            formData.append(field, ''); // signal backend to clear this field
          }
        });
        body = formData;
        // Do NOT set Content-Type — browser sets it with the correct boundary
      } else {
        headers = { 'Content-Type': 'application/json' };
        const { theme_gradient, theme_primary_color, theme_secondary_color, theme_background_color, theme_text_color, ...rest } = values;
        // For removed images, set them to null in JSON payload
        const imageOverrides = {};
        ['logo', 'banner', 'mobile_logo', 'mobile_banner'].forEach((field) => {
          if (removedImages[field]) imageOverrides[field] = null;
        });
        body = JSON.stringify({ ...rest, theme: themeObj, ...imageOverrides });
      }

      const response = await fetch(url, { method, headers, body });

      const data = await response.json();
      if (response.ok || data.success || data.code) {
        message.success(
          `Business Type ${editingBusinessType ? 'updated' : 'created'} successfully!`
        );
        setIsModalVisible(false);
        fetchBusinessTypes();
      } else {
        message.error(
          data.message ||
          `Failed to ${editingBusinessType ? 'update' : 'create'} Business Type`
        );
      }
    } catch (error) {
      console.error('Error saving business type:', error);
      message.error('An error occurred while saving');
    } finally {
      setSaving(false);
    }
  };

  // ── Table Columns ──────────────────────────────────────────────────────────

  const columns = [
    {
      title: 'Code',
      dataIndex: 'code',
      key: 'code',
      width: 80,
      render: (val) => <Tag color="blue">{val}</Tag>,
    },
    {
      title: 'SVG Icon',
      dataIndex: 'svg',
      key: 'svg',
      width: 70,
      render: (svg) =>
        svg ? (
          <div
            style={{ width: 24, height: 24, color: '#555' }}
            dangerouslySetInnerHTML={{ __html: svg }}
          />
        ) : (
          '—'
        ),
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      render: (val) => <Text strong>{val}</Text>,
    },
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      render: (val) => val || <Text type="secondary">—</Text>,
    },
    {
      title: 'Caption',
      dataIndex: 'caption',
      key: 'caption',
      render: (val) => val || <Text type="secondary">—</Text>,
    },
    {
      title: 'Categories',
      dataIndex: 'categories',
      key: 'categories',
      render: (cats) =>
        cats && cats.length > 0 ? (
          <Text type="secondary">{cats.length} categories</Text>
        ) : (
          '—'
        ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status) => (
        <Tag color={status ? 'green' : 'red'}>
          {status ? 'ACTIVE' : 'INACTIVE'}
        </Tag>
      ),
    },
    {
      title: 'Action',
      key: 'action',
      width: 220,
      render: (_, record) => (
        <Space>
          <Button
            icon={<EyeOutlined />}
            size="small"
            onClick={() => handleView(record)}
          >
            View
          </Button>
          <Button
            type="primary"
            icon={<EditOutlined />}
            size="small"
            onClick={() => handleEdit(record)}
          >
            Edit
          </Button>
          <Popconfirm
            title="Are you sure you want to delete this Business Type?"
            onConfirm={() => handleDelete(record.code)}
            okText="Yes"
            cancelText="No"
          >
            <Button danger icon={<DeleteOutlined />} size="small">
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // ── View Modal Content ─────────────────────────────────────────────────────

  const renderViewModal = () => {
    const bt = viewingBusinessType;
    if (!bt) return null;

    const theme = bt.theme || {};

    return (
      <Modal
        title={
          <Space>
            <AppstoreOutlined style={{ color: '#52c41a' }} />
            <span>{bt.type}</span>
            <Tag color="blue">{bt.code}</Tag>
          </Space>
        }
        open={isViewModalVisible}
        onCancel={() => setIsViewModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setIsViewModalVisible(false)}>
            Close
          </Button>,
          <Button
            key="edit"
            type="primary"
            icon={<EditOutlined />}
            onClick={() => {
              setIsViewModalVisible(false);
              handleEdit(bt);
            }}
          >
            Edit
          </Button>,
        ]}
        width={780}
        styles={{ body: { maxHeight: '65vh', overflowY: 'auto', paddingRight: 4 } }}
      >
        {/* Basic Info */}
        <Descriptions
          bordered
          size="small"
          column={2}
          style={{ marginBottom: 16 }}
        >
          <Descriptions.Item label="Code">{bt.code || '—'}</Descriptions.Item>
          <Descriptions.Item label="Status">
            <Tag color={bt.status ? 'green' : 'red'}>
              {bt.status ? 'ACTIVE' : 'INACTIVE'}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Type" span={2}>
            {bt.type || '—'}
          </Descriptions.Item>
          <Descriptions.Item label="Title">{bt.title || '—'}</Descriptions.Item>
          <Descriptions.Item label="Caption">{bt.caption || '—'}</Descriptions.Item>
          <Descriptions.Item label="Created At">
            {bt.created_at ? new Date(bt.created_at).toLocaleString() : '—'}
          </Descriptions.Item>
          <Descriptions.Item label="Updated At">
            {bt.updated_at ? new Date(bt.updated_at).toLocaleString() : '—'}
          </Descriptions.Item>
        </Descriptions>

        {/* Order Types */}
        <Divider orientation="left" orientationMargin={0}>Order Types</Divider>
        <Space wrap style={{ marginBottom: 16 }}>
          {bt.order_types && bt.order_types.length > 0
            ? bt.order_types.map((ot) => (
              <Tag key={ot} color="purple">
                {ot}
              </Tag>
            ))
            : <Text type="secondary">None</Text>}
        </Space>

        {/* Categories */}
        <Divider orientation="left" orientationMargin={0}>Categories</Divider>
        <Space wrap style={{ marginBottom: 16 }}>
          {bt.categories && bt.categories.length > 0
            ? bt.categories.map((cat, i) => (
              <Tag key={i} color="geekblue">
                {cat}
              </Tag>
            ))
            : <Text type="secondary">None</Text>}
        </Space>

        {/* Theme */}
        <Divider orientation="left" orientationMargin={0}>Theme</Divider>

        {/* Color swatches — always dark text so they're readable */}
        <Row gutter={[8, 8]} style={{ marginBottom: 16 }}>
          {/* Gradient — full width */}
          <Col xs={24}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '6px 10px',
                borderRadius: 6,
                border: '1px solid #f0f0f0',
                background: '#fafafa',
              }}
            >
              <div
                style={{
                  width: 24,
                  height: 24,
                  borderRadius: 4,
                  flexShrink: 0,
                  backgroundImage: theme.gradient,
                  backgroundColor: '#eee',
                  border: '1px solid rgba(0,0,0,0.08)',
                }}
              />
              <div style={{ overflow: 'hidden' }}>
                <div style={{ fontSize: 11, color: '#888', lineHeight: 1.2 }}>Gradient</div>
                <div style={{ fontSize: 12, color: '#222', fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {theme.gradient || '—'}
                </div>
              </div>
            </div>
          </Col>

          {/* Individual color swatches */}
          {[
            { label: 'Primary', key: 'primary_color' },
            { label: 'Secondary', key: 'secondary_color' },
            { label: 'Background', key: 'background_color' },
            { label: 'Text', key: 'text_color' },
          ].map(({ label, key }) => (
            <Col xs={12} key={key}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '6px 10px',
                  borderRadius: 6,
                  border: '1px solid #f0f0f0',
                  background: '#fafafa',
                }}
              >
                <div
                  style={{
                    width: 24,
                    height: 24,
                    borderRadius: 4,
                    flexShrink: 0,
                    background: theme[key] || '#eee',
                    border: '1px solid rgba(0,0,0,0.08)',
                  }}
                />
                <div>
                  <div style={{ fontSize: 11, color: '#888', lineHeight: 1.2 }}>{label}</div>
                  <div style={{ fontSize: 13, color: '#222', fontWeight: 500 }}>
                    {theme[key] || '—'}
                  </div>
                </div>
              </div>
            </Col>
          ))}
        </Row>

        {/* SVG Icon */}
        {bt.svg && (
          <>
            <Divider orientation="left" orientationMargin={0}>SVG Icon</Divider>
            <div
              style={{
                width: 64, height: 64, marginBottom: 16,
                color: theme.primary_color || '#555',
              }}
              dangerouslySetInnerHTML={{ __html: bt.svg }}
            />
          </>
        )}

        {/* Images */}
        <Divider orientation="left" orientationMargin={0}>Images</Divider>
        <Row gutter={[12, 12]} style={{ marginBottom: 8 }}>
          {[
            { label: 'Logo', url: getImageUrl(bt.logo) },
            { label: 'Banner', url: getImageUrl(bt.banner) },
            { label: 'Mobile Logo', url: getImageUrl(bt.mobile_logo) },
            { label: 'Mobile Banner', url: getImageUrl(bt.mobile_banner) },
          ].map(({ label, url }) => (
            <Col xs={12} md={6} key={label}>
              <div style={{ textAlign: 'center' }}>
                <Text
                  type="secondary"
                  style={{ display: 'block', fontSize: 11, marginBottom: 4 }}
                >
                  {label}
                </Text>
                {url ? (
                  <a href={url} target="_blank" rel="noopener noreferrer" title={`Open ${label} in new tab`}>
                    <img
                      src={url}
                      alt={label}
                      referrerPolicy="no-referrer"
                      style={{
                        width: '100%',
                        height: 80,
                        objectFit: 'cover',
                        borderRadius: 6,
                        border: '1px solid #f0f0f0',
                        cursor: 'pointer',
                        display: 'block',
                      }}
                    />
                  </a>
                ) : (
                  <div
                    style={{
                      height: 80,
                      background: '#f5f5f5',
                      borderRadius: 6,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#bbb',
                      fontSize: 12,
                    }}
                  >
                    No Image
                  </div>
                )}
              </div>
            </Col>
          ))}
        </Row>
      </Modal>
    );
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div style={{ width: '100%' }}>
      {/* Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '24px',
          flexWrap: 'wrap',
          gap: '16px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <AppstoreOutlined style={{ fontSize: '28px', color: '#52c41a' }} />
          <Title level={2} style={{ color: '#2c3e50', margin: 0 }}>
            Business Types Management
          </Title>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
            Add Business Type
          </Button>
          <Button icon={<ArrowLeftOutlined />} onClick={() => setView('menu')}>
            Back
          </Button>
        </div>
      </div>

      {/* Table */}
      <Card
        bordered={false}
        style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.05)', borderRadius: '12px' }}
      >
        <Table
          columns={columns}
          dataSource={businessTypes}
          rowKey="code"
          loading={loading}
          pagination={{ pageSize: 10 }}
          scroll={{ x: 'max-content' }}
        />
      </Card>

      {/* View Detail Modal */}
      {renderViewModal()}

      {/* Add / Edit Modal */}
      <Modal
        title={editingBusinessType ? 'Edit Business Type' : 'Add Business Type'}
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        onOk={() => form.submit()}
        confirmLoading={saving}
        width={720}
        styles={{ body: { maxHeight: '70vh', overflowY: 'auto', paddingRight: 4 } }}
      >
        <Form form={form} layout="vertical" onFinish={handleModalSubmit}>

          {/* ── Basic Info ──────────────────────────────────── */}
          <Divider orientation="left" orientationMargin={0} style={{ marginTop: 0 }}>Basic Info</Divider>
          <Row gutter={16}>
            <Col xs={12}>
              <Form.Item
                name="code"
                label="Code"
                rules={[{ required: true, message: 'Please enter a code (e.g. R01)' }]}
              >
                <Input placeholder="e.g. R01" disabled={!!editingBusinessType} />
              </Form.Item>
            </Col>
            <Col xs={12}>
              <Form.Item
                name="type"
                label="Business Type"
                rules={[{ required: true, message: 'Please enter the business type' }]}
              >
                <Input placeholder="e.g. Retail and Wholesale" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={12}>
              <Form.Item name="title" label="Title">
                <Input placeholder="e.g. Grocery Mart" />
              </Form.Item>
            </Col>
            <Col xs={12}>
              <Form.Item name="caption" label="Caption">
                <Input placeholder="e.g. Fresh Groceries" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24}>
              <Form.Item name="status" label="Active Status" valuePropName="checked">
                <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
              </Form.Item>
            </Col>
          </Row>

          {/* ── Categories & Order Types ─────────────────── */}
          <Divider orientation="left" orientationMargin={0}>Categories & Order Types</Divider>
          <Row gutter={16}>
            <Col xs={24}>
              <Form.Item name="categories" label="Categories">
                <Select
                  mode="tags"
                  style={{ width: '100%' }}
                  placeholder="Type a category and press Enter"
                  tokenSeparators={[',']}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24}>
              <Form.Item name="order_types" label="Order Types">
                <Select
                  mode="multiple"
                  style={{ width: '100%' }}
                  placeholder="Select order types"
                  options={[
                    { label: 'Delivery', value: 'delivery' },
                    { label: 'Pickup', value: 'pickup' },
                    { label: 'Pick Up', value: 'pick_up' },
                    { label: 'Dine In', value: 'dine_in' },
                    { label: 'Takeaway', value: 'takeaway' },
                  ]}
                />
              </Form.Item>
            </Col>
          </Row>

          {/* ── SVG ─────────────────────────────────────── */}
          <Divider orientation="left" orientationMargin={0}>SVG Icon</Divider>
          <Row gutter={16}>
            <Col xs={24}>
              <Form.Item name="svg" label="SVG Markup">
                <Input.TextArea rows={4} placeholder="<svg ...>...</svg>" />
              </Form.Item>
            </Col>
          </Row>

          {/* ── Images (Upload) ──────────────────────────── */}
          <Divider orientation="left" orientationMargin={0}>Images</Divider>
          <Row gutter={[16, 0]}>
            {[
              { field: 'logo', label: 'Logo', urlKey: 'logo' },
              { field: 'banner', label: 'Banner', urlKey: 'banner' },
              { field: 'mobile_logo', label: 'Mobile Logo', urlKey: 'mobile_logo' },
              { field: 'mobile_banner', label: 'Mobile Banner', urlKey: 'mobile_banner' },
            ].map(({ field, label, urlKey }) => {
              const existingUrl = getImageUrl(editingBusinessType?.[urlKey]);
              const selectedFile = imageFiles[field];
              const isRemoved = removedImages[field];
              // Show preview only if not removed
              const previewSrc = !isRemoved
                ? (selectedFile ? URL.createObjectURL(selectedFile) : existingUrl || null)
                : null;
              const hasAnything = !!previewSrc || !!selectedFile;

              return (
                <Col xs={12} key={field} style={{ marginBottom: 16 }}>
                  <div style={{ fontWeight: 500, marginBottom: 6, fontSize: 14 }}>{label}</div>

                  {/* Thumbnail preview */}
                  {previewSrc ? (
                    <div style={{ marginBottom: 8 }}>
                      <img
                        src={previewSrc}
                        alt={label}
                        referrerPolicy="no-referrer"
                        style={{
                          width: '100%', height: 80,
                          objectFit: 'cover',
                          borderRadius: 6,
                          border: '1px solid #f0f0f0',
                          display: 'block',
                        }}
                      />
                    </div>
                  ) : (
                    <div
                      style={{
                        width: '100%', height: 80,
                        background: '#fafafa',
                        border: '1px dashed #d9d9d9',
                        borderRadius: 6,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: isRemoved ? '#ff4d4f' : '#bbb',
                        fontSize: 12,
                        marginBottom: 8,
                      }}
                    >
                      {isRemoved ? 'Will be removed' : 'No Image'}
                    </div>
                  )}

                  {/* Upload & Remove buttons */}
                  <Space size={4}>
                    <Upload
                      beforeUpload={(file) => {
                        setImageFiles((prev) => ({ ...prev, [field]: file }));
                        setRemovedImages((prev) => ({ ...prev, [field]: false }));
                        return false;
                      }}
                      showUploadList={false}
                      accept="image/*"
                      maxCount={1}
                    >
                      <Button icon={<UploadOutlined />} size="small">
                        {selectedFile && !isRemoved ? 'Change' : previewSrc ? 'Replace' : 'Upload'}
                      </Button>
                    </Upload>

                    {/* Remove: show if there's a new file OR an existing URL that hasn't been removed */}
                    {(selectedFile || (existingUrl && !isRemoved)) && (
                      <Button
                        size="small"
                        danger
                        onClick={() => {
                          setImageFiles((prev) => ({ ...prev, [field]: null }));
                          setRemovedImages((prev) => ({ ...prev, [field]: true }));
                        }}
                      >
                        Remove
                      </Button>
                    )}

                    {/* Undo remove */}
                    {isRemoved && !selectedFile && (
                      <Button
                        size="small"
                        onClick={() =>
                          setRemovedImages((prev) => ({ ...prev, [field]: false }))
                        }
                      >
                        Undo
                      </Button>
                    )}
                  </Space>
                </Col>
              );
            })}
          </Row>

          {/* ── Theme ───────────────────────────────────── */}
          <Divider orientation="left" orientationMargin={0}>Theme</Divider>
          <Row gutter={16}>
            <Col xs={24}>
              <Form.Item name="theme_gradient" label="Gradient">
                <Input placeholder="e.g. linear-gradient(135deg, #FF9800 0%, #F57C00 100%)" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={12}>
              <Form.Item name="theme_primary_color" label="Primary Color">
                <Input placeholder="e.g. #FF5252" />
              </Form.Item>
            </Col>
            <Col xs={12}>
              <Form.Item name="theme_secondary_color" label="Secondary Color">
                <Input placeholder="e.g. #F57C00" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={12}>
              <Form.Item name="theme_background_color" label="Background Color">
                <Input placeholder="e.g. #4DD0E1" />
              </Form.Item>
            </Col>
            <Col xs={12}>
              <Form.Item name="theme_text_color" label="Text Color">
                <Input placeholder="e.g. #FFFFFF" />
              </Form.Item>
            </Col>
          </Row>

        </Form>
      </Modal>
    </div>
  );
};

export default BusinessTypes;
